import { z } from 'zod'

// Document status enum
export const documentStatusSchema = z.enum(['pending', 'processing', 'ready', 'error'])
export type DocumentStatus = z.infer<typeof documentStatusSchema>

// Document schema
export const documentSchema = z.object({
  id: z.string().uuid(),
  knowledge_base_id: z.string().uuid(),
  filename: z.string(),
  file_type: z.string(),
  file_size: z.number(),
  status: documentStatusSchema,
  error_message: z.string().nullable(),
  chunk_count: z.number().default(0),
  metadata: z.record(z.unknown()).default({}),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
})

export type Document = z.infer<typeof documentSchema>

// Document upload error schema
export const documentUploadErrorSchema = z.object({
  filename: z.string(),
  error: z.string(),
})

export type DocumentUploadError = z.infer<typeof documentUploadErrorSchema>

// Document upload response schema
export const documentUploadResponseSchema = z.object({
  documents: z.array(documentSchema),
  uploaded_count: z.number(),
  failed_count: z.number(),
  errors: z.array(documentUploadErrorSchema),
})

export type DocumentUploadResponse = z.infer<typeof documentUploadResponseSchema>

// Document list response schema
export const documentListResponseSchema = z.object({
  documents: z.array(documentSchema),
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
  total_pages: z.number(),
})

export type DocumentListResponse = z.infer<typeof documentListResponseSchema>

// Query params for listing documents
export interface DocumentListParams {
  status?: DocumentStatus
  search?: string
  page?: number
  page_size?: number
}

// Allowed file types
export const ALLOWED_FILE_TYPES = ['pdf', 'docx', 'txt', 'md', 'csv', 'xlsx'] as const
export type AllowedFileType = (typeof ALLOWED_FILE_TYPES)[number]

// Max file size in MB
export const MAX_FILE_SIZE_MB = 10
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

// Mime types mapping
export const MIME_TYPE_MAP: Record<string, AllowedFileType> = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'text/plain': 'txt',
  'text/markdown': 'md',
  'text/csv': 'csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
}

// File type labels for display
export const FILE_TYPE_LABELS: Record<AllowedFileType, string> = {
  pdf: 'PDF',
  docx: 'Word',
  txt: 'Texto',
  md: 'Markdown',
  csv: 'CSV',
  xlsx: 'Excel',
}

// Document status labels and colors
export const DOCUMENT_STATUS_CONFIG: Record<DocumentStatus, { label: string; color: string }> = {
  pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-800' },
  processing: { label: 'Processando', color: 'bg-blue-100 text-blue-800' },
  ready: { label: 'Pronto', color: 'bg-green-100 text-green-800' },
  error: { label: 'Erro', color: 'bg-red-100 text-red-800' },
}

// Helper function to validate file before upload
export function validateFile(file: File): string | null {
  // Check file size
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `Arquivo excede o limite de ${MAX_FILE_SIZE_MB}MB`
  }

  // Get file extension
  const extension = file.name.split('.').pop()?.toLowerCase()
  if (!extension || !ALLOWED_FILE_TYPES.includes(extension as AllowedFileType)) {
    return `Tipo de arquivo nao permitido: ${extension}. Tipos aceitos: ${ALLOWED_FILE_TYPES.join(', ')}`
  }

  return null
}

// Helper function to format file size
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

// Document chunk preview schema (without embedding)
export const documentChunkPreviewSchema = z.object({
  chunk_index: z.number(),
  content: z.string(),
})

export type DocumentChunkPreview = z.infer<typeof documentChunkPreviewSchema>

// Document preview response schema
export const documentPreviewResponseSchema = z.object({
  id: z.string().uuid(),
  filename: z.string(),
  status: documentStatusSchema,
  error_message: z.string().nullable(),
  chunks: z.array(documentChunkPreviewSchema),
  total_chunks: z.number(),
  has_more: z.boolean(),
})

export type DocumentPreviewResponse = z.infer<typeof documentPreviewResponseSchema>
